import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { SearchIcon, Search } from "lucide-react";
import SmallLoadingSpinner from "../assets/SmallLoadingSpinner";
import ItemSearchModal from "../components/ItemSearchModal";
import { useItemCache } from "../context/ItemCacheContext";

interface CachedItemSelectProps {
  value?: string;
  displayValue?: string; // Direct display value - skips cache lookup for fast rendering
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  label?: boolean | string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSelect?: () => void;  // Called after item is selected - use to move focus to next field
  enableRef?: boolean;
  searchBy?: 'name' | 'code';
  disabled?: boolean;
  spreadsheetMode?: boolean;  // Enables debounced dropdown behavior
  minSearchLength?: number;   // Minimum chars before showing dropdown (default 0)
  debounceMs?: number;        // Debounce delay in ms (default 300)
  accountingTypeCodePrefix?: string;
}

const CachedItemSelect = forwardRef<HTMLInputElement, CachedItemSelectProps>(
  ({ value, displayValue, onChange, placeholder, required = false, onKeyDown, onSelect, enableRef = false, label, searchBy = 'name', disabled = false, spreadsheetMode = false, minSearchLength = 0, debounceMs = 300, accountingTypeCodePrefix }, ref) => {
    const { getItem, searchItems, isLoading, isPreloading } = useItemCache();
    const { t } = useTranslation();

    const [searchTerm, setSearchTerm] = useState("");
    const [isWaitingForCache, setIsWaitingForCache] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const isTypingRef = useRef(false);

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, [enableRef]);

    // Update search term when value changes (but not while typing)
    useEffect(() => {
      if (isTypingRef.current) return;
      if (!value) {
        setSearchTerm("");
        return;
      }
      // If displayValue is provided, use it directly (fast path - no API calls)
      if (displayValue !== undefined) {
        setSearchTerm(displayValue);
        return;
      }
      const item = getItem(value);
      if (item) {
        setSearchTerm(searchBy === 'code' ? (item.ident || "") : (item.name || ""));
      } else if (!isPreloading) {
        // Show loading state while waiting for cache
        setIsWaitingForCache(true);

        // Wait 300ms for bulk preload to potentially start/complete before making individual call
        const timeoutId = setTimeout(() => {
          // Check cache again after delay
          const cachedItem = getItem(value);
          if (cachedItem) {
            if (!isTypingRef.current) {
              setSearchTerm(searchBy === 'code' ? (cachedItem.ident || "") : (cachedItem.name || ""));
            }
            setIsWaitingForCache(false);
            return;
          }

          // Only make individual API call if still not in cache
          const apiUrl = process.env.REACT_APP_API_URL ?? '';
          const token = localStorage.getItem('token');
          fetch(`${apiUrl}/api/items/${value}`, { headers: { 'x-auth-token': token ?? '' } })
            .then(res => res.json())
            .then(data => {
              if (!isTypingRef.current) {
                if (data && data._id) {
                  setSearchTerm(searchBy === 'code' ? (data.ident || "") : (data.name || ""));
                } else {
                  setSearchTerm("");
                }
              }
            })
            .catch(() => { if (!isTypingRef.current) setSearchTerm(""); })
            .finally(() => setIsWaitingForCache(false));
        }, 300);

        return () => {
          clearTimeout(timeoutId);
          setIsWaitingForCache(false);
        };
      }
      // If isPreloading is true, wait for it to finish - the useEffect will re-run when isPreloading changes
    }, [value, displayValue, getItem, searchBy, isPreloading]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      isTypingRef.current = true;
      setSearchTerm(val);

      if (val === "") {
        onChange("");
        return;
      }
    };

    const handleSelect = (itemId: string, itemIdent: string, itemName: string) => {
      onChange(itemId);
      setSearchTerm(searchBy === 'code' ? itemIdent : itemName);
      isTypingRef.current = false;
      // Call onSelect callback to allow parent to move focus to next field
      if (onSelect) {
        setTimeout(() => onSelect(), 0);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // F4 key: open search modal
      if (e.key === 'F4') {
        e.preventDefault();
        setIsModalOpen(true);
        return;
      }

      // Call parent onKeyDown handler for other keys
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    // Render compact mode for tables
    if (label === false) {
      return (
        <div className="w-full h-6">
          <div className="relative h-6">
            {/* Search icon (left side) - opens modal */}
            <button
              className="absolute left-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation();
                setIsModalOpen(true);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              title="Search (F4)"
              disabled={disabled}
            >
              <Search size={14} />
            </button>

            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onBlur={() => {
                isTypingRef.current = false;
              }}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              className="w-full h-6 pl-5 pr-1 bg-transparent border-0 outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoComplete="off"
              placeholder=""
            />

            {/* Show loading spinner when waiting for cache */}
            {(isWaitingForCache || isPreloading) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                <SmallLoadingSpinner size="xs" />
              </div>
            )}
          </div>

          {/* Search Modal */}
          <ItemSearchModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSelect={(item) => {
              handleSelect(item._id, item.ident || "", item.name || "");
            }}
            currentValue={value}
            accountingTypeCodePrefix={accountingTypeCodePrefix}
          />
        </div>
      );
    }

    // Full mode
    return (
      <div className="w-full">
        {/* Label for full mode */}
        {typeof label === 'string' && (
          <label className="block text-sm font-semibold mb-2 text-gray-800 dark:text-gray-200">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <div
            className={`flex items-center border rounded-lg bg-white dark:bg-gray-800 h-10 transition-all duration-200 ${
              disabled
                ? "cursor-not-allowed bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 cursor-text"
            }`}
            onClick={() => !disabled && inputRef.current?.focus()}
          >
            {/* Search Icon - flex child - clickable to open modal */}
            <button
              className="flex-shrink-0 pl-3 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-500 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) setIsModalOpen(true);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              title="Search (F4)"
              disabled={disabled}
            >
              <SearchIcon size={16} />
            </button>

            {/* Input - flex grow */}
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onBlur={() => {
                isTypingRef.current = false;
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t("common.search") || "Search..."}
              className={`flex-1 py-1.5 pl-2 pr-2 text-sm bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none ${
                disabled ? 'cursor-not-allowed text-gray-500 dark:text-gray-600' : ''
              }`}
              autoComplete="off"
              disabled={disabled}
            />

            {/* Clear Button - flex child */}
            {(value || searchTerm) && !disabled && (
              <button
                onClick={() => {
                  isTypingRef.current = true;
                  onChange("");
                  setSearchTerm("");
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
                className="flex-shrink-0 pr-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors text-xl leading-none"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Search Modal */}
        <ItemSearchModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelect={(item) => {
            handleSelect(item._id, item.ident || "", item.name || "");
          }}
          currentValue={value}
          accountingTypeCodePrefix={accountingTypeCodePrefix}
        />
      </div>
    );
  }
);

export default CachedItemSelect;
